import { useRef, type CSSProperties, type ReactElement, type ReactNode, type Ref } from "react";
import { classes } from "./classes.ts";
import { useCentred } from "./measure.ts";
import { logical } from "./metrics.ts";

/**
 * A row of boxes set in the middle of the width it is given on whole logical pixels, by measurement: CSS centring
 * (`justify-content: center`, `margin: auto`) puts a box half a pixel off the grid whenever the space left over is
 * odd. The kit's centred parts (a dialog's actions and error line, a notice's icon, detail and actions, an empty
 * state's glyph, a full-width button's label) are each one of these.
 */

/**
 * Sets a ref to an element, whichever kind of ref it is.
 *
 * @param ref The ref, or undefined.
 * @param element The element, or null as it leaves the page.
 */
function assign(ref: Ref<HTMLElement> | undefined, element: HTMLElement | null): void {
  if (typeof ref === "function") {
    ref(element);
  } else if (ref !== undefined && ref !== null) {
    ref.current = element;
  }
}

/**
 * A centred row: an area as wide as its container holding the row, as wide as its content up to the area, placed
 * at `floor((area - row) / 2)` logical pixels from the area's left and placed again whenever either changes size.
 * Until it is measured, and in static markup, the row sits at the area's left.
 *
 * @param props The row's content; its `className`, `data-kit` name and `style`, which the row carries; `rowRef`,
 *   given the row's element; and `inline`, which draws the area and the row as spans, for inside a button.
 * @returns The area holding the row.
 * @example
 * <CentredRow className="ot-dialog__actions" kit="dialog-actions" style={{ gap: logical(6) }}>{buttons}</CentredRow>
 */
export function CentredRow({ children, className, kit, style, rowRef, inline = false }: { readonly children: ReactNode; readonly className?: string; readonly kit?: string; readonly style?: CSSProperties; readonly rowRef?: Ref<HTMLElement>; readonly inline?: boolean }): ReactElement {
  const area = useRef<HTMLElement | null>(null);
  const row = useRef<HTMLElement | null>(null);
  const place = useCentred(area, row, { x: true, y: false }, null);
  const Tag = inline ? "span" : "div";
  return (
    <Tag
      ref={(element: HTMLElement | null) => {
        area.current = element;
      }}
      className="ot-centred"
    >
      <Tag
        ref={(element: HTMLElement | null) => {
          row.current = element;
          assign(rowRef, element);
        }}
        className={classes("ot-centred__row", className)}
        data-kit={kit}
        style={{ ...style, left: logical(place.left) }}
      >
        {children}
      </Tag>
    </Tag>
  );
}
